import { useObserver } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';
import BackButton from './BackButton';
import HomeButton from './HomeButton';

const SwitchButton = React.lazy(() => import('./SwitchButton'));
const MobileControls = React.lazy(() => import('../Header/MobileControls'));
const DesktopControls = React.lazy(() => import('../Header/DesktopControls'));

const OverlapMenuHeader = ({ selectedTab, tabClick }) => {
  const {
    header: {
      height,
      background,
      color,
      title: { content: headerTitle, color: titleColor }
    },
    window: { borderRadius },
    overlapMenu: {
      chatOptions,
      size: { headerSidePadding }
    }
  } = useConfig();
  const { ui } = useStores();
  const { t } = useTranslation();

  // Data for switch buttons
  const headerButtons = [
    {
      title: 'overlapMenu.chatTabButton',
      isOpenedChatTab: true,
      selected: selectedTab,
      position: chatOptions.headerChatButton.position
    },
    {
      title: 'overlapMenu.generalTabButton',
      isOpenedChatTab: false,
      selected: !selectedTab,
      position: 0
    }
  ];

  return useObserver(() => (
    <Wrapper
      className="react-rnd-draggable"
      height={height}
      sidePadding={headerSidePadding}
      color={color}
      background={background}
      borderRadius={borderRadius}
    >
      {headerTitle && <Title textColor={titleColor}>{headerTitle}</Title>}

      {chatOptions.enabled && (
        <ButtonsWrapper>
          {headerButtons
            .sort((a, b) => a.position - b.position)
            .map(button => {
              return (
                <SwitchButton
                  key={button.title}
                  onClick={() => tabClick(button.isOpenedChatTab)}
                  selected={button.selected}
                >
                  {t(button.title)}
                </SwitchButton>
              );
            })}
        </ButtonsWrapper>
      )}
      <ButtonsWrapper className="react-rnd-draggable-cancel">
        <BackButton />
        <HomeButton />
        {ui.isMobile ? <MobileControls /> : <DesktopControls />}
      </ButtonsWrapper>
    </Wrapper>
  ));
};

OverlapMenuHeader.propTypes = {
  selectedTab: PropTypes.bool.isRequired,
  tabClick: PropTypes.func.isRequired
};

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${({ height }) => `${height}px`};
  padding-top: 12px;
  padding-bottom: 12px;
  padding-right: ${props => (props.sidePadding ? props.sidePadding : 8)}px;
  padding-left: ${props => (props.sidePadding ? props.sidePadding : 8)}px;
  font-family: ${props => props.fontFamily};
  color: ${props => props.color};
  background: ${props => props.background};
  border-radius: ${({ borderRadius }) => `${borderRadius}px ${borderRadius}px 0 0`};
`;

const Title = styled.h3`
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: 16px !important;
  font-weight: 400;
  color: ${props => props.textColor};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  &:not(:last-of-type) {
    margin-right: 6px;
  }
  & > div:nth-of-type(n + 2) {
    margin-left: 14px;
  }
  & button {
    cursor: pointer;
  }
`;

export default OverlapMenuHeader;
